import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Loading from "Components/Loading/Loading";
import { Typography, Box, Button } from "@mui/material";
import { OAUTH_REDIRECT_URL } from "features/auth/constants";
import { SharedTokenInjectedProps } from "HoC/withSharedToken";
import {
  authenticateSyncClient,
  linkSyncClientToProject,
} from "features/dataConnectors/utils/syncAuth";
import { ApiService, ApiShareLinkType, ApiSyncType } from "@incendium/api";
import { useNotification } from "Hooks";
import { logout } from "Actions";
import { useDispatch } from "react-redux";
import { useSelectedProject } from "Hooks";

type OAuthSyncProps = Partial<SharedTokenInjectedProps>;

type OAuthSyncMode = "shopify-share" | "popup";

function OAuthSyncCenteredMessage({
  title,
  body,
}: {
  title?: ReactNode;
  body: string;
}) {
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        textAlign: "center",
        gap: title ? 1.5 : 0,
      }}
    >
      {title && (
        <Typography variant="h5" component="h1" gutterBottom>
          {title}
        </Typography>
      )}
      <Typography variant="body1">{body}</Typography>
    </Box>
  );
}

function OAuthSync({
  shareToken,
  shareTokenError,
  shareTokenLoading,
  shareTokenPayload,
}: OAuthSyncProps) {
  const location = useLocation();
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const [processing, setProcessing] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [callbackDelivered, setCallbackDelivered] = useState(false);
  const hasAttemptedSharedSync = useRef(false);
  const dispatch = useDispatch();
  const { wipeLastSelected } = useSelectedProject();
  const hasLoggedOutRef = useRef(false);

  const mode: OAuthSyncMode = shareToken ? "shopify-share" : "popup";

  const handleLogout = useCallback(() => {
    wipeLastSelected();
    dispatch(logout());
  }, [dispatch, wipeLastSelected]);

  useEffect(() => {
    if (completed && !hasLoggedOutRef.current) {
      hasLoggedOutRef.current = true;
      handleLogout();
    }
  }, [completed, handleLogout]);

  const callbackParams = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    const success = urlParams.get("success");
    // WooCommerce returns user_id (our pending id); OAuth services use code.
    const code = urlParams.get("code") || urlParams.get("user_id");
    const shopParam = urlParams.get("shop");
    return {
      code,
      shop: shopParam,
      denied: success === "0",
    };
  }, [location.search]);

  useEffect(() => {
    if (mode !== "popup") {
      return;
    }

    const { code, denied } = callbackParams;

    if (denied) {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: "oauth-callback-denied" },
          window.location.origin,
        );
      }
      setTimeout(() => {
        window.close();
      }, 100);
      return;
    }

    if (!code) {
      return;
    }

    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        {
          type: "oauth-callback",
          code,
        },
        window.location.origin,
      );
    } else {
      localStorage.setItem("oauth-callback-code", code);
      localStorage.setItem("oauth-callback-timestamp", Date.now().toString());

      if (window.opener) {
        try {
          window.opener.focus();
        } catch {
          // ignore cross-origin focus errors
        }
      }
    }

    setCallbackDelivered(true);

    setTimeout(() => {
      window.close();
    }, 100);
  }, [callbackParams, mode]);

  useEffect(() => {
    if (mode !== "shopify-share") {
      return;
    }

    const { code, shop } = callbackParams;

    if (
      !code ||
      !shareToken ||
      !shareTokenPayload?.projectId ||
      shareTokenPayload.type !== ApiShareLinkType.SHARE_LINK_TYPE_SYNC ||
      shareTokenError ||
      processing ||
      completed ||
      hasAttemptedSharedSync.current
    ) {
      return;
    }

    const redirectUrl = OAUTH_REDIRECT_URL;

    const run = async () => {
      hasAttemptedSharedSync.current = true;
      setProcessing(true);
      setProcessingError(null);

      try {
        const oauthClient = await authenticateSyncClient({
          service: ApiService.SHOPIFY,
          code,
          redirectUrl,
          shop,
        });

        if (!oauthClient?.id) {
          throw new Error("Missing oauth client");
        }

        await linkSyncClientToProject({
          projectId: shareTokenPayload.projectId as number,
          oauthClientId: oauthClient.id as number,
          type: ApiSyncType.SHOPIFY_API,
        });

        setCompleted(true);
        showSuccessNotification("Shopify store connected successfully.");
      } catch (error) {
        console.error("Failed to complete Shopify OAuth", error);
        const message =
          "We couldn't complete the Shopify connection. Please refresh and try again.";
        setProcessingError(message);
        showErrorNotification(message);
      } finally {
        setProcessing(false);
      }
    };

    run();
  }, [
    mode,
    callbackParams,
    shareToken,
    shareTokenPayload,
    shareTokenError,
    processing,
    completed,
    showErrorNotification,
    showSuccessNotification,
  ]);

  if (shareTokenError) {
    return (
      <OAuthSyncCenteredMessage body={shareTokenError} />
    );
  }

  if (processingError) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          textAlign: "center",
          gap: 2,
        }}
      >
        <Typography variant="body1">{processingError}</Typography>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  if (completed) {
    return (
      <OAuthSyncCenteredMessage
        title={
          <>
            Shopify Store Connected{" "}
            <span role="img" aria-label="celebration">
              🎉
            </span>
          </>
        }
        body="Connection complete. You can now close this window."
      />
    );
  }

  if (mode === "popup") {
    if (callbackParams.denied) {
      return (
        <OAuthSyncCenteredMessage body="Authentication was cancelled. You can close this window." />
      );
    }

    if (callbackDelivered || callbackParams.code) {
      return (
        <OAuthSyncCenteredMessage body="Authentication complete. Returning you to the app..." />
      );
    }

    return (
      <Loading fullHeight text="Completing authentication..." />
    );
  }

  if (shareTokenLoading || processing || !callbackParams.code) {
    return (
      <Loading
        fullHeight
        text={
          callbackParams.code
            ? "Connecting your Shopify store..."
            : "Waiting for Shopify authentication to complete..."
        }
      />
    );
  }

  return (
    <OAuthSyncCenteredMessage body="Waiting for Shopify authentication to complete..." />
  );
}

export default OAuthSync;
